<template>
  <div>
    <div class="page-header page-header-small">
      <parallax
        class="page-header-image"
        style="background-image: url('/img/gallery.jpg')"
      >
      </parallax>
      <div class="content-center">
        <div class="container">
          <h1 class="title">Gallery</h1>
        </div>
      </div>
    </div>

    <div class="section gallery-section">
      <div class="container">
        <div class="gallery-toolbar">
          <div class="gallery-search">
            <i class="now-ui-icons ui-1_zoom-bold"></i>
            <input
              v-model.trim="searchQuery"
              type="text"
              class="form-control"
              placeholder="사진 제목, 장소, 태그, 메모로 검색"
            />
          </div>
          <p class="gallery-result-count">
            {{ currentCount }}개의 항목
            <span v-if="searchQuery">· "{{ searchQuery }}" 검색 결과</span>
          </p>
        </div>

        <div class="gallery-view-toggle" role="group" aria-label="갤러리 보기 선택">
          <button
            type="button"
            class="gallery-view-btn"
            :class="{ active: viewMode === 'photos' }"
            @click="setViewMode('photos')"
          >
            전체 사진
          </button>
          <button
            type="button"
            class="gallery-view-btn"
            :class="{ active: viewMode === 'albums' }"
            @click="setViewMode('albums')"
          >
            앨범 목록
          </button>
        </div>

        <div class="separator separator-primary"></div>

        <div v-if="viewMode === 'photos'" class="photo-view">
          <div class="photo-grid">
            <button
              v-for="photo in filteredPhotos"
              :key="photo.id"
              type="button"
              class="photo-tile"
              :class="{ active: selectedPhotoId === photo.id }"
              @click="openPhotoModal(photo.id)"
            >
              <img :src="photo.image" :alt="photo.title" />
            </button>
          </div>

          <div v-if="!filteredPhotos.length" class="gallery-empty text-center">
            <h4>사진을 찾을 수 없습니다.</h4>
            <p>다른 키워드로 다시 검색해보세요.</p>
          </div>
        </div>

        <div v-else>
          <div v-if="activeAlbum" class="album-photo-view">
            <div class="album-view-header">
              <button
                type="button"
                class="album-back-btn"
                @click="closeAlbum"
              >
                <i class="now-ui-icons arrows-1_minimal-left"></i>
                앨범 목록
              </button>

              <div class="album-view-copy">
                <p>{{ activeAlbum.period }}</p>
                <div class="album-title-line">
                  <h2>{{ activeAlbum.title }}</h2>
                  <span>{{ activeAlbum.photos.length }} photos</span>
                </div>
                <small>{{ activeAlbum.description }}</small>
              </div>
            </div>

            <div class="photo-grid">
              <button
                v-for="photo in filteredAlbumPhotos"
                :key="photo.id"
                type="button"
                class="photo-tile"
                :class="{ active: selectedPhotoId === photo.id }"
                @click="openPhotoModal(photo.id)"
              >
                <img :src="photo.image" :alt="photo.title" />
              </button>
            </div>

            <div v-if="!filteredAlbumPhotos.length" class="gallery-empty text-center">
              <h4>사진을 찾을 수 없습니다.</h4>
              <p>다른 키워드로 다시 검색해보세요.</p>
            </div>
          </div>

          <div v-else class="row gallery-grid-row">
            <div
              v-for="album in filteredAlbums"
              :key="album.id"
              class="col-lg-4 col-md-6"
            >
              <button
                type="button"
                class="album-card"
                @click="openAlbum(album.id)"
              >
                <div
                  class="album-card-cover"
                  :style="{ backgroundImage: `url('${album.coverImage}')` }"
                >
                  <span>{{ album.photos.length }} photos</span>
                </div>
                <div class="album-card-body">
                  <p>{{ album.period }}</p>
                  <h3>{{ album.title }}</h3>
                  <small>{{ album.description }}</small>
                </div>
              </button>
            </div>
          </div>

          <div
            v-if="!activeAlbum && !filteredAlbums.length"
            class="gallery-empty text-center"
          >
            <h4>앨범을 찾을 수 없습니다.</h4>
            <p>앨범 이름, 기간, 설명으로 검색해보세요.</p>
          </div>
        </div>
      </div>
    </div>

    <modal
      :show.sync="modals.photo"
      modalClasses="gallery-photo-modal"
      @close="closePhotoModal"
    >
      <template slot="base-content">
        <div v-if="selectedPhoto" class="gallery-lightbox">
          <button
            type="button"
            class="lightbox-close"
            aria-label="사진 닫기"
            @click="closePhotoModal"
          >
            <i class="now-ui-icons ui-1_simple-remove"></i>
          </button>

          <figure class="lightbox-photo-stage">
            <button
              type="button"
              class="lightbox-nav lightbox-nav-prev"
              aria-label="이전 사진"
              @click="showPreviousPhoto"
            >
              <i class="now-ui-icons arrows-1_minimal-left"></i>
            </button>

            <img
              :src="selectedPhoto.image"
              :alt="selectedPhoto.title"
              class="lightbox-photo"
            />

            <button
              type="button"
              class="lightbox-nav lightbox-nav-next"
              aria-label="다음 사진"
              @click="showNextPhoto"
            >
              <i class="now-ui-icons arrows-1_minimal-right"></i>
            </button>
          </figure>

          <aside class="lightbox-meta">
            <div class="lightbox-meta-header">
              <span class="album-avatar">{{ selectedPhoto.albumTitle.charAt(0) }}</span>
              <div>
                <p>{{ selectedPhoto.albumTitle }}</p>
                <strong>{{ selectedPhoto.title }}</strong>
              </div>
            </div>

            <div class="metadata-list">
              <dl>
                <div>
                  <dt>촬영일</dt>
                  <dd>{{ selectedPhoto.takenAt }}</dd>
                </div>
                <div>
                  <dt>장소</dt>
                  <dd>{{ selectedPhoto.location }}</dd>
                </div>
                <div>
                  <dt>카메라</dt>
                  <dd>{{ selectedPhoto.camera }}</dd>
                </div>
              </dl>

              <div class="metadata-block">
                <span class="metadata-label">태그</span>
                <div class="metadata-tags">
                  <span
                    v-for="tag in selectedPhotoTags"
                    :key="`${selectedPhoto.id}-${tag}`"
                  >
                    #{{ tag }}
                  </span>
                </div>
              </div>

              <div class="metadata-block">
                <span class="metadata-label">메모</span>
                <p>{{ selectedPhoto.memo }}</p>
              </div>
            </div>
          </aside>
        </div>
      </template>
    </modal>
  </div>
</template>

<script>
import { Modal, Parallax } from '@/components';
import galleryAlbums from './data';

export default {
  name: 'gallery-page',
  bodyClass: 'landing-page',
  components: {
    Modal,
    Parallax
  },
  data() {
    const albums = galleryAlbums.map(album => ({
      ...album,
      photos: album.photos.map(photo => ({
        ...photo,
        albumId: album.id,
        albumTitle: album.title
      }))
    }));

    return {
      albums,
      viewMode: 'photos',
      searchQuery: '',
      selectedAlbumId: null,
      selectedPhotoId: albums[0].photos[0].id,
      modals: {
        photo: false
      }
    };
  },
  computed: {
    photos() {
      return this.albums.reduce(
        (photos, album) => photos.concat(album.photos),
        []
      );
    },
    filteredPhotos() {
      const keyword = this.searchQuery.toLowerCase();

      if (!keyword) {
        return this.photos;
      }

      return this.photos.filter(photo =>
        [
          photo.title,
          photo.albumTitle,
          photo.takenAt,
          photo.location,
          photo.camera,
          photo.tagsText,
          photo.memo
        ].some(value =>
          String(value)
            .toLowerCase()
            .includes(keyword)
        )
      );
    },
    filteredAlbums() {
      const keyword = this.searchQuery.toLowerCase();

      if (!keyword) {
        return this.albums;
      }

      return this.albums.filter(album =>
        [
          album.title,
          album.period,
          album.description,
          ...album.photos.map(photo => photo.title),
          ...album.photos.map(photo => photo.tagsText)
        ].some(value =>
          String(value)
            .toLowerCase()
            .includes(keyword)
        )
      );
    },
    activeAlbum() {
      return this.albums.find(album => album.id === this.selectedAlbumId);
    },
    filteredAlbumPhotos() {
      if (!this.activeAlbum) {
        return [];
      }

      const keyword = this.searchQuery.toLowerCase();

      if (!keyword) {
        return this.activeAlbum.photos;
      }

      return this.activeAlbum.photos.filter(photo =>
        [
          photo.title,
          photo.albumTitle,
          photo.takenAt,
          photo.location,
          photo.camera,
          photo.tagsText,
          photo.memo
        ].some(value =>
          String(value)
            .toLowerCase()
            .includes(keyword)
        )
      );
    },
    modalPhotos() {
      if (this.viewMode === 'albums' && this.activeAlbum) {
        return this.filteredAlbumPhotos.length
          ? this.filteredAlbumPhotos
          : this.activeAlbum.photos;
      }

      return this.filteredPhotos.length ? this.filteredPhotos : this.photos;
    },
    selectedPhoto() {
      return this.photos.find(photo => photo.id === this.selectedPhotoId);
    },
    selectedPhotoTags() {
      if (!this.selectedPhoto || !this.selectedPhoto.tagsText) {
        return [];
      }

      return this.selectedPhoto.tagsText
        .split(',')
        .map(tag => tag.trim())
        .filter(Boolean);
    },
    currentCount() {
      if (this.viewMode === 'photos') {
        return this.filteredPhotos.length;
      }

      return this.activeAlbum
        ? this.filteredAlbumPhotos.length
        : this.filteredAlbums.length;
    }
  },
  mounted() {
    this.applyRouteQuery();
  },
  methods: {
    setViewMode(mode) {
      this.viewMode = mode;
      this.selectedAlbumId = null;
      this.updateGalleryQuery(mode);
    },
    openAlbum(albumId) {
      this.selectedAlbumId = albumId;
      this.searchQuery = '';

      const album = this.albums.find(item => item.id === albumId);
      if (album && album.photos.length) {
        this.selectedPhotoId = album.photos[0].id;
      }

      this.updateGalleryQuery('albums', albumId);
    },
    closeAlbum() {
      this.selectedAlbumId = null;
      this.updateGalleryQuery('albums');
    },
    openPhotoModal(photoId) {
      this.selectedPhotoId = photoId;
      this.modals.photo = true;
    },
    closePhotoModal() {
      this.modals.photo = false;
    },
    showAdjacentPhoto(direction) {
      const photos = this.modalPhotos;
      const currentIndex = photos.findIndex(
        photo => photo.id === this.selectedPhotoId
      );

      if (currentIndex === -1 || !photos.length) return;

      const nextIndex =
        (currentIndex + direction + photos.length) % photos.length;
      this.selectedPhotoId = photos[nextIndex].id;
    },
    showPreviousPhoto() {
      this.showAdjacentPhoto(-1);
    },
    showNextPhoto() {
      this.showAdjacentPhoto(1);
    },
    findAlbumByQuery(value) {
      return this.albums.find(album => album.id === value);
    },
    applyRouteQuery() {
      const { v, a } = this.$route.query;
      const isAlbumView = v === 'album' || v === 'albums';

      if (!isAlbumView) {
        this.viewMode = 'photos';
        this.selectedAlbumId = null;
        return;
      }

      this.viewMode = 'albums';

      if (!a) {
        this.selectedAlbumId = null;
        return;
      }

      const album = this.findAlbumByQuery(String(a));
      this.selectedAlbumId = album ? album.id : null;

      if (album && album.photos.length) {
        this.selectedPhotoId = album.photos[0].id;
      }
    },
    updateGalleryQuery(mode, albumId = null) {
      const query = mode === 'albums' ? { v: 'album' } : {};
      const album = albumId
        ? this.albums.find(item => item.id === albumId)
        : null;

      if (album) {
        query.a = album.id;
      }

      if (
        this.$route.query.v === query.v &&
        this.$route.query.a === query.a
      ) {
        return;
      }

      this.$router
        .push({
          name: 'gallery',
          query
        })
        .catch(() => {});
    }
  },
  watch: {
    '$route.query'() {
      this.applyRouteQuery();
    },
    filteredPhotos(photos) {
      if (!photos.length) return;

      const hasSelectedPhoto = photos.some(
        photo => photo.id === this.selectedPhotoId
      );

      if (!hasSelectedPhoto) {
        this.selectedPhotoId = photos[0].id;
      }
    }
  }
};
</script>

<style scoped>
.gallery-section {
  padding-bottom: 72px;
}

.gallery-toolbar {
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 20px;
  flex-wrap: wrap;
  margin-bottom: 28px;
}

.gallery-search {
  position: relative;
  flex: 1 1 420px;
  max-width: 560px;
}

.gallery-search i {
  position: absolute;
  top: 50%;
  left: 18px;
  color: #adb5bd;
  transform: translateY(-50%);
}

.gallery-search .form-control {
  height: 54px;
  padding-left: 48px;
  margin-bottom: 0;
  border: 1px solid rgba(0, 0, 0, 0.08);
  border-radius: 999px;
}

.gallery-result-count {
  margin-bottom: 0;
  color: #6c757d;
  font-weight: 600;
}

.gallery-view-toggle {
  display: inline-flex;
  gap: 6px;
  padding: 6px;
  margin-bottom: 30px;
  border: 1px solid rgba(0, 0, 0, 0.08);
  border-radius: 999px;
  background: #fff;
  box-shadow: 0 14px 30px rgba(44, 62, 80, 0.08);
}

.gallery-view-btn {
  min-width: 118px;
  padding: 10px 18px;
  border: 0;
  border-radius: 999px;
  background: transparent;
  color: #495057;
  font-size: 0.9rem;
  font-weight: 700;
  transition:
    background-color 0.2s ease,
    color 0.2s ease,
    box-shadow 0.2s ease;
}

.gallery-view-btn.active {
  background: #2ca8ff;
  color: #fff;
  box-shadow: 0 10px 22px rgba(44, 168, 255, 0.28);
}

.photo-view {
  display: grid;
  gap: 34px;
}

.album-card-body p {
  margin-bottom: 8px;
  color: #2ca8ff;
  font-size: 0.78rem;
  font-weight: 800;
  letter-spacing: 0.08em;
  text-transform: uppercase;
}

.album-card-body h3 {
  margin-bottom: 0;
  font-size: 1.35rem;
  line-height: 1.35;
}

:deep(.modal-dialog.gallery-photo-modal) {
  width: min(1180px, calc(100vw - 32px));
  max-width: none;
  margin: 24px auto;
}

:deep(.gallery-photo-modal .modal-content) {
  overflow: hidden;
  border: 0;
  border-radius: 4px;
  background: transparent;
}

:deep(.modal.show) {
  background-color: rgba(0, 0, 0, 0.78);
}

.gallery-lightbox {
  position: relative;
  display: grid;
  grid-template-columns: minmax(0, 1fr) 360px;
  min-height: min(760px, calc(100vh - 48px));
  max-height: calc(100vh - 48px);
  overflow: hidden;
  border-radius: 4px;
  background: #fff;
}

.lightbox-close {
  position: absolute;
  top: 14px;
  right: 14px;
  z-index: 2;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 34px;
  height: 34px;
  border: 0;
  border-radius: 50%;
  background: rgba(0, 0, 0, 0.58);
  color: #fff;
}

.lightbox-photo-stage {
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
  min-width: 0;
  min-height: 0;
  margin: 0;
  background: #050505;
}

.lightbox-photo {
  display: block;
  width: 100%;
  height: 100%;
  max-height: calc(100vh - 48px);
  object-fit: contain;
}

.lightbox-nav {
  position: absolute;
  top: 50%;
  z-index: 2;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 44px;
  height: 44px;
  border: 0;
  border-radius: 50%;
  background: rgba(0, 0, 0, 0.52);
  color: #fff;
  font-size: 1.25rem;
  transform: translateY(-50%);
  transition:
    background-color 0.2s ease,
    opacity 0.2s ease;
}

.lightbox-nav:hover {
  background: rgba(0, 0, 0, 0.72);
}

.lightbox-nav-prev {
  left: 18px;
}

.lightbox-nav-next {
  right: 18px;
}

.lightbox-meta {
  display: flex;
  overflow-y: auto;
  flex-direction: column;
  border-left: 1px solid rgba(0, 0, 0, 0.08);
  background: #fff;
}

.lightbox-meta-header {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 20px;
  border-bottom: 1px solid rgba(0, 0, 0, 0.08);
}

.album-avatar {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 38px;
  height: 38px;
  border-radius: 50%;
  background: #2ca8ff;
  color: #fff;
  font-weight: 800;
}

.lightbox-meta-header p {
  margin-bottom: 2px;
  color: #6c757d;
  font-size: 0.78rem;
  font-weight: 700;
}

.lightbox-meta-header strong {
  display: block;
  color: #2c2c2c;
  font-size: 1rem;
  line-height: 1.35;
}

.metadata-list {
  display: grid;
  gap: 18px;
  padding: 20px;
}

.metadata-list dl {
  display: grid;
  gap: 0;
  margin: 0;
  overflow: hidden;
  border: 1px solid rgba(44, 62, 80, 0.1);
  border-radius: 8px;
}

.metadata-list dl > div {
  display: grid;
  grid-template-columns: 86px minmax(0, 1fr);
  border-bottom: 1px solid rgba(44, 62, 80, 0.08);
}

.metadata-list dl > div:last-child {
  border-bottom: 0;
}

.metadata-list dt,
.metadata-list dd {
  margin: 0;
  padding: 12px 14px;
}

.metadata-list dt,
.metadata-label {
  color: #6c757d;
  font-size: 0.78rem;
  font-weight: 800;
  letter-spacing: 0.08em;
  text-transform: uppercase;
}

.metadata-list dd {
  color: #2c2c2c;
  font-weight: 700;
}

.metadata-block {
  display: grid;
  gap: 10px;
}

.metadata-tags {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
}

.metadata-tags span {
  padding: 7px 11px;
  border-radius: 999px;
  background: rgba(44, 168, 255, 0.1);
  color: #1683d8;
  font-size: 0.82rem;
  font-weight: 800;
}

.metadata-block p {
  margin: 0;
  color: #495057;
  line-height: 1.75;
}

.photo-grid {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 4px;
}

.photo-tile {
  display: block;
  overflow: hidden;
  padding: 0;
  border: 0;
  border-radius: 0;
  background: #111820;
  aspect-ratio: 1 / 1;
}

.photo-tile img {
  display: block;
  width: 100%;
  height: 100%;
  object-fit: cover;
  transition:
    opacity 0.2s ease,
    transform 0.2s ease;
}

.photo-tile:hover img,
.photo-tile.active img {
  opacity: 0.72;
  transform: scale(1.03);
}

.photo-tile.active {
  box-shadow: inset 0 0 0 3px #2ca8ff;
}

.album-photo-view {
  display: grid;
  gap: 20px;
}

.album-view-header {
  display: grid;
  grid-template-columns: auto minmax(0, 1fr);
  gap: 22px;
  align-items: center;
  padding: 20px 22px;
  border: 1px solid rgba(44, 62, 80, 0.09);
  border-radius: 8px;
  background: #fff;
  box-shadow: 0 14px 30px rgba(44, 62, 80, 0.08);
}

.album-view-copy {
  display: flex;
  flex-direction: column;
  min-width: 0;
}

.album-view-copy p {
  margin-bottom: 6px;
  color: #2ca8ff;
  font-size: 0.78rem;
  font-weight: 800;
  letter-spacing: 0.08em;
  text-transform: uppercase;
}

.album-view-copy small {
  max-width: 720px;
  color: #6c757d;
  line-height: 1.65;
}

.album-title-line {
  display: flex;
  align-items: center;
  gap: 10px;
  flex-wrap: wrap;
  margin-bottom: 6px;
}

.album-title-line h2 {
  margin-bottom: 0;
  font-size: 1.95rem;
  line-height: 1.3;
}

.album-title-line span {
  padding: 6px 10px;
  border-radius: 999px;
  background: rgba(44, 168, 255, 0.1);
  color: #1683d8;
  font-size: 0.75rem;
  font-weight: 800;
  letter-spacing: 0.04em;
  text-transform: uppercase;
}

.album-back-btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  min-width: 104px;
  padding: 11px 14px;
  border: 1px solid rgba(44, 62, 80, 0.12);
  border-radius: 999px;
  background: #fff;
  color: #495057;
  font-size: 0.9rem;
  font-weight: 800;
}

.gallery-grid-row > [class*='col-'] {
  margin-bottom: 30px;
}

.album-card-body small {
  color: #6c757d;
  line-height: 1.65;
}

.album-card {
  display: block;
  width: 100%;
  height: 100%;
  overflow: hidden;
  padding: 0;
  border: 0;
  border-radius: 8px;
  background: #fff;
  color: inherit;
  text-align: left;
  box-shadow: 0 20px 45px rgba(44, 62, 80, 0.12);
  transition:
    box-shadow 0.2s ease,
    transform 0.2s ease;
}

.album-card:hover {
  transform: translateY(-4px);
  box-shadow: 0 24px 54px rgba(44, 62, 80, 0.17);
}

.album-card-cover {
  position: relative;
  min-height: 230px;
  background-position: center;
  background-size: cover;
}

.album-card-cover::before {
  content: '';
  position: absolute;
  inset: 0;
  background: linear-gradient(
    180deg,
    rgba(15, 23, 42, 0.04) 0%,
    rgba(15, 23, 42, 0.65) 100%
  );
}

.album-card-cover span {
  position: absolute;
  right: 18px;
  bottom: 16px;
  z-index: 1;
  color: #fff;
  font-size: 0.78rem;
  font-weight: 800;
  letter-spacing: 0.08em;
  text-transform: uppercase;
}

.album-card-body {
  padding: 22px;
}

.gallery-empty {
  padding: 34px 0 10px;
}

.gallery-empty p {
  color: #6c757d;
}

@media (max-width: 991px) {
  .gallery-lightbox {
    grid-template-columns: 1fr;
    overflow-y: auto;
  }

  .lightbox-photo-stage {
    min-height: 58vh;
  }

  .lightbox-photo {
    height: auto;
    max-height: 58vh;
  }

  .lightbox-nav {
    width: 38px;
    height: 38px;
  }

  .lightbox-meta {
    border-top: 1px solid rgba(0, 0, 0, 0.08);
    border-left: 0;
  }

}

@media (max-width: 767px) {
  :deep(.modal-dialog.gallery-photo-modal) {
    width: calc(100vw - 12px);
    margin: 6px auto;
  }

  .gallery-lightbox {
    max-height: calc(100vh - 12px);
  }

  .gallery-toolbar {
    margin-bottom: 24px;
  }

  .gallery-view-toggle {
    display: flex;
    width: 100%;
  }

  .gallery-view-btn {
    flex: 1;
    min-width: 0;
  }

  .album-view-header {
    grid-template-columns: 1fr;
    gap: 14px;
    padding: 18px;
  }

  .album-back-btn {
    justify-content: flex-start;
    width: max-content;
  }

  .album-title-line h2 {
    font-size: 1.55rem;
  }

  .gallery-grid-row > [class*='col-'] {
    margin-bottom: 22px;
  }
}
</style>
